import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getAuthSessionRequestHeaders } from '../../utilities/authSessionRequest';
import { FieldControl } from 'spark-core-dx/components';
import { useAuthContext, useCoreContext, useCoreStateContext } from 'spark-core-dx/contexts';
import { useCorePermissions } from 'spark-core-dx/hooks';
import { ItemService, SystemTypes } from 'spark-core-dx/services';
import DialogControl from '../convoke/ConvokeDialogControl';
import './helpSupport.scss';

const SUPPORT_NAVIGATION_TARGET = 'platform-help-support';
const SUPPORT_REQUEST_TYPE = 'Support';
const SUPPORT_DIALOG_VARIANTS = Object.freeze({
  support: Object.freeze({
    title: 'Help & Support',
    subTitle: 'Tell us what you need help with. We’ll automatically include technical details from this page. It may take up to a minute for AI to review your request and notify a system administrator.',
    submitLabel: 'Send Request',
    fieldLabel: 'How can we assist you?',
    fieldDescription: 'Share what happened, what you were trying to do, and what you expected to happen.',
    responseLabel: 'Support response',
    includedLabel: 'Included diagnostics',
  }),
  sales: Object.freeze({
    title: 'Sales Support',
    subTitle: 'Tell us what you’re interested in. We’ll include the Marketplace page you’re viewing so our team has the right product context. It may take up to a minute for AI to review your message and create an in-app notification for the appropriate administrator.',
    submitLabel: 'Send Sales Request',
    fieldLabel: 'How can our sales team help?',
    fieldDescription: 'Share your goals, pricing questions, implementation timeline, or anything else you would like to discuss.',
    responseLabel: 'Sales request status',
    includedLabel: 'Included request context',
  }),
});
const SUPPORT_DIAGNOSTIC_TEXT_LIMIT = 1000;
const SUPPORT_DIAGNOSTIC_ERROR_LIMIT = 20;
const SUPPORT_SENSITIVE_ASSIGNMENT_PATTERN = /(authorization|cookie|password|secret|token|api[_-]?key|accountkey|accesskey|sharedaccesssignature|code)\s*[:=]\s*([^\s,;&]+)/gi;
const SUPPORT_BEARER_PATTERN = /bearer\s+[a-z0-9._~+\-/]+=*/gi;
const SUPPORT_JWT_PATTERN = /\beyJ[a-zA-Z0-9_-]{8,}\.[a-zA-Z0-9_-]{8,}\.[a-zA-Z0-9_-]{8,}\b/g;
const SUPPORT_QUERY_SECRET_PATTERN = /([?&](?:code|token|access_token|refresh_token|id_token|client_secret)=)[^&#\s]+/gi;

const EMPTY_AUTH_SESSION = Object.freeze({
  authenticated: false,
  idleExpiresAtUtc: null,
  absoluteExpiresAtUtc: null,
  checkedAtUtc: null,
});

const recentBrowserErrors = [];
let browserErrorCaptureReferenceCount = 0;
let originalConsoleError = null;

const HelpSupportContext = createContext(null);

export const cleanSupportDiagnosticText = (value, maximumLength = SUPPORT_DIAGNOSTIC_TEXT_LIMIT) => {
  const raw = String(value ?? '').trim();
  const sanitized = raw
    .replace(SUPPORT_BEARER_PATTERN, 'Bearer [REDACTED]')
    .replace(SUPPORT_JWT_PATTERN, '[REDACTED JWT]')
    .replace(SUPPORT_QUERY_SECRET_PATTERN, '$1[REDACTED]')
    .replace(SUPPORT_SENSITIVE_ASSIGNMENT_PATTERN, '$1=[REDACTED]');
  return sanitized.length <= maximumLength ? sanitized : sanitized.slice(0, maximumLength);
};

const appendBrowserError = (source, message) => {
  const cleanedMessage = cleanSupportDiagnosticText(message);
  if (!cleanedMessage) return;

  recentBrowserErrors.push({
    Source: cleanSupportDiagnosticText(source, 100) || 'browser',
    Message: cleanedMessage,
    TimestampUtc: new Date().toISOString(),
  });
  if (recentBrowserErrors.length > SUPPORT_DIAGNOSTIC_ERROR_LIMIT) {
    recentBrowserErrors.splice(0, recentBrowserErrors.length - SUPPORT_DIAGNOSTIC_ERROR_LIMIT);
  }
};

const getSafeConsoleErrorMessage = (values) => values
  .map((value) => {
    if (value instanceof Error) return value.message;
    if (['string', 'number', 'boolean'].includes(typeof value)) return String(value);
    return '[non-text console detail omitted]';
  })
  .join(' ');

const installBrowserErrorCapture = () => {
  if (typeof window === 'undefined') return () => {};

  browserErrorCaptureReferenceCount += 1;
  if (browserErrorCaptureReferenceCount > 1) {
    return () => {
      browserErrorCaptureReferenceCount = Math.max(0, browserErrorCaptureReferenceCount - 1);
    };
  }

  const handleWindowError = (event) => {
    appendBrowserError('window.error', event?.error?.message ?? event?.message);
  };
  const handleUnhandledRejection = (event) => {
    appendBrowserError(
      'unhandledrejection',
      event?.reason instanceof Error ? event.reason.message : event?.reason,
    );
  };

  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  originalConsoleError = console.error;
  console.error = (...values) => {
    appendBrowserError('console.error', getSafeConsoleErrorMessage(values));
    originalConsoleError?.apply(console, values);
  };

  return () => {
    browserErrorCaptureReferenceCount = Math.max(0, browserErrorCaptureReferenceCount - 1);
    if (browserErrorCaptureReferenceCount > 0) return;

    window.removeEventListener('error', handleWindowError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    if (originalConsoleError) {
      console.error = originalConsoleError;
      originalConsoleError = null;
    }
  };
};

const normalizeAuthSession = (value) => ({
  authenticated: value?.authenticated === true || value?.Authenticated === true,
  idleExpiresAtUtc: value?.idleExpiresAtUtc ?? value?.IdleExpiresAtUtc ?? null,
  absoluteExpiresAtUtc: value?.absoluteExpiresAtUtc ?? value?.AbsoluteExpiresAtUtc ?? null,
  checkedAtUtc: value?.checkedAtUtc ?? value?.CheckedAtUtc ?? new Date().toISOString(),
});

const loadAuthSession = async () => {
  try {
    const response = await window.fetch('/api/Core/AuthSession', {
      credentials: 'include',
      headers: getAuthSessionRequestHeaders({ Accept: 'application/json' }),
    });
    if (!response.ok) return normalizeAuthSession(EMPTY_AUTH_SESSION);
    return normalizeAuthSession(await response.json());
  } catch {
    return normalizeAuthSession(EMPTY_AUTH_SESSION);
  }
};

export const buildBrowserDiagnosticContent = (authSession) => {
  let timeZone = '';
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    timeZone = '';
  }

  return {
    Browser: {
      Origin: cleanSupportDiagnosticText(window.location.origin, 300),
      Path: cleanSupportDiagnosticText(`${window.location.pathname}${window.location.search}`, 1000),
      Title: cleanSupportDiagnosticText(document.title, 300),
      UserAgent: cleanSupportDiagnosticText(navigator.userAgent, 600),
      Language: cleanSupportDiagnosticText(navigator.language, 50),
      Online: navigator.onLine,
      VisibilityState: cleanSupportDiagnosticText(document.visibilityState, 50),
      ViewportWidth: window.innerWidth,
      ViewportHeight: window.innerHeight,
      TimeZone: cleanSupportDiagnosticText(timeZone, 100),
      CapturedAtUtc: new Date().toISOString(),
    },
    AuthSession: {
      Authenticated: authSession?.authenticated === true,
      IdleExpiresAtUtc: authSession?.idleExpiresAtUtc ?? null,
      AbsoluteExpiresAtUtc: authSession?.absoluteExpiresAtUtc ?? null,
      CheckedAtUtc: authSession?.checkedAtUtc ?? null,
    },
    RecentErrors: recentBrowserErrors.map((error) => ({ ...error })),
  };
};

const getFirstResult = (response) => response?.first?.() ?? response?.[0] ?? response;
const getResultItem = (result) => (
  result?.Items?.first?.()
  ?? result?.Items?.[0]
  ?? result?.Item
  ?? result?.Value
  ?? null
);

const HelpSupportDialog = ({
  open,
  variant,
  message,
  onMessageChange,
  requestState,
  onSubmit,
  onClose,
}) => {
  const dialogCopy = SUPPORT_DIALOG_VARIANTS[variant] ?? SUPPORT_DIALOG_VARIANTS.support;

  return (
    <DialogControl
      className="help-support-dialog"
      openDialog={open}
      title={dialogCopy.title}
      subTitle={requestState.success ? null : dialogCopy.subTitle}
      okText={requestState.loading ? 'Sending...' : requestState.success ? 'Sent' : dialogCopy.submitLabel}
      disableOk={requestState.loading || requestState.success === true}
      cancelText={requestState.success ? 'Close' : 'Cancel'}
      keepOpenOnSuccess
      onOk={onSubmit}
      onCancel={onClose}
      onX={onClose}
    >
      <div className="help-support-dialog__content">
        {requestState.success === true ? (
          <div className={`help-support-dialog__note help-support-dialog__note--${requestState.success ? 'success' : 'error'}${variant === 'sales' ? ' help-support-dialog__note--borderless' : ''}`}>
            <strong>{dialogCopy.responseLabel}</strong>
            <span className="help-support-dialog__response">{requestState.message}</span>
            {requestState.result?.LogId ? <span>Request ID: {requestState.result.LogId}</span> : null}
            {requestState.result?.AIReviewStatus ? <span>AI review: {requestState.result.AIReviewStatus}</span> : null}
            {variant !== 'sales' && requestState.result?.NotificationStatus ? <span>In-app notification: {requestState.result.NotificationStatus}</span> : null}
            {variant !== 'sales' && requestState.result?.EmailStatus ? <span>Email notification: {requestState.result.EmailStatus}</span> : null}
          </div>
        ) : (
          <>
            <FieldControl
              fieldMetadata={{
                FieldType: SystemTypes.UIFieldType.TextArea,
                FieldName: 'SupportHelp',
                DisplayName: dialogCopy.fieldLabel,
                Description: dialogCopy.fieldDescription,
                IsRequired: false,
                MaxLength: 4000,
              }}
              groupId="helpSupportRequest"
              value={message}
              onChange={onMessageChange}
            />

            <section className="help-support-dialog__included convoke-panel" aria-label={dialogCopy.includedLabel}>
              <strong>{dialogCopy.includedLabel}</strong>
              {variant === 'sales' ? (
                <ul>
                  <li>The Marketplace page and product or resource named in your request</li>
                  <li>Safe account and environment details needed to route your request</li>
                  <li>Basic browser details that can help if the Marketplace experience is not working</li>
                </ul>
              ) : (
                <ul>
                  <li>Current route, browser/runtime state, and viewport information</li>
                  <li>Safe authentication-session status and expiration timestamps</li>
                  <li>Recent window, unhandled-promise, and text-only console errors captured while Status is open</li>
                  <li>Server-side environment, database-routing, and storage-mode summary</li>
                </ul>
              )}
              <small>Page contents, form values, browser storage, cookies, headers, and tokens are not collected.</small>
            </section>
          </>
        )}
      </div>
    </DialogControl>
  );
};

export const HelpSupportProvider = ({ children }) => {
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const { isAuthorized } = useAuthContext();
  const { canExecute, canView } = useCorePermissions();
  const canUseSupport = isAuthorized === true
    && canView('SupportRequest')
    && canExecute('SupportRequest');
  const authSessionRequestId = useRef(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogVariant, setDialogVariant] = useState('support');
  const [supportHelp, setSupportHelp] = useState('');
  const [source, setSource] = useState('Application');
  const [authSession, setAuthSession] = useState(EMPTY_AUTH_SESSION);
  const [supportRequest, setSupportRequest] = useState({
    loading: false,
    success: null,
    message: null,
    result: null,
  });

  const openHelpSupport = useCallback((options = {}) => {
    if (!canUseSupport) return false;

    const requestId = authSessionRequestId.current + 1;
    authSessionRequestId.current = requestId;
    setDialogVariant(options?.variant === 'sales' ? 'sales' : 'support');
    setSupportHelp(String(options?.initialMessage ?? ''));
    setSource(String(options?.source ?? 'Application').trim() || 'Application');
    setSupportRequest({ loading: false, success: null, message: null, result: null });
    setDialogOpen(true);

    if (options?.authSession) {
      setAuthSession(normalizeAuthSession(options.authSession));
      return;
    }

    setAuthSession(normalizeAuthSession(EMPTY_AUTH_SESSION));
    loadAuthSession().then((nextAuthSession) => {
      if (authSessionRequestId.current === requestId) setAuthSession(nextAuthSession);
    });
    return true;
  }, [canUseSupport]);

  const closeHelpSupport = useCallback(() => {
    authSessionRequestId.current += 1;
    setDialogOpen(false);
    setDialogVariant('support');
    setSupportHelp('');
    setSupportRequest({ loading: false, success: null, message: null, result: null });
  }, []);

  const submitSupportRequest = useCallback(async () => {
    if (!canUseSupport) {
      const message = 'You do not have permission to submit support requests.';
      setSupportRequest({ loading: false, success: false, message, result: null });
      return { Success: false, DisplayMessage: message };
    }

    setSupportRequest({ loading: true, success: null, message: null, result: null });

    try {
      const service = new ItemService('Items/SupportRequest', coreState, coreContext, 'CORE', 60000, true);
      const response = await service.postOperation({
        operationType: 'SubmitSupportRequest',
        item: {
          Type: SUPPORT_REQUEST_TYPE,
          Message: supportHelp,
          Content: JSON.stringify(buildBrowserDiagnosticContent(authSession)),
          Source: source,
        },
        isItem: true,
        isWarning: true,
        supressAllErrors: true,
      });
      const result = getFirstResult(response);
      const resultItem = getResultItem(result);
      if (result?.Success !== true || !resultItem) {
        throw new Error(
          result?.DisplayMessage
          ?? result?.Message?.DisplayMessage
          ?? 'The support request could not be submitted.',
        );
      }

      setSupportRequest({
        loading: false,
        success: true,
        message: resultItem.UserResponse
          ?? 'Your request was saved and sent through the support options available for this system.',
        result: resultItem,
      });
      return result;
    } catch (error) {
      const message = error?.message ?? 'The support request could not be submitted.';
      setSupportRequest({ loading: false, success: false, message, result: null });
      return { Success: false, DisplayMessage: message };
    }
  }, [authSession, canUseSupport, coreContext, coreState, source, supportHelp]);

  useEffect(() => installBrowserErrorCapture(), []);

  useEffect(() => {
    const setCustomNavigationItems = coreContext?.setCustomNavigationItems;
    if (!setCustomNavigationItems) return undefined;

    if (!canUseSupport) {
      setCustomNavigationItems(SUPPORT_NAVIGATION_TARGET, []);
      return undefined;
    }

    setCustomNavigationItems(SUPPORT_NAVIGATION_TARGET, [{
      Key: 'help-support',
      DisplayName: 'Help & Support',
      Icon: 'support_agent',
      Placement: 'utility',
      Sequence: 1000,
      AriaHasPopup: 'dialog',
      onClick: openHelpSupport,
    }]);

    return () => setCustomNavigationItems(SUPPORT_NAVIGATION_TARGET, []);
  }, [canUseSupport, coreContext?.setCustomNavigationItems, openHelpSupport]);

  const contextValue = useMemo(() => ({
    openHelpSupport,
    supportRequestLoading: supportRequest.loading,
  }), [openHelpSupport, supportRequest.loading]);

  return (
    <HelpSupportContext.Provider value={contextValue}>
      {children}
      <HelpSupportDialog
        open={dialogOpen}
        variant={dialogVariant}
        message={supportHelp}
        onMessageChange={setSupportHelp}
        requestState={supportRequest}
        onSubmit={submitSupportRequest}
        onClose={closeHelpSupport}
      />
    </HelpSupportContext.Provider>
  );
};

export const useHelpSupport = () => {
  const context = useContext(HelpSupportContext);
  if (!context) throw new Error('useHelpSupport must be used within a HelpSupportProvider');
  return context;
};
